/**
 * A scheduled match counts as played once it is completed, has a winner, or is in progress.
 * Same effective-status rule as `api/admin/event/[eventId]/match-schedule/move/route.ts`:
 * the linked `match` row wins over the schedule row's own status.
 *
 * Accepts the client row (`MatchScheduleWithPlayers`, which carries the `match:match_id(...)` join
 * at runtime but does not type it) and the server-fetched shape alike.
 */
export interface PlayableScheduleRow {
  status?: string | null;
  match?: { status?: string | null; winner_id?: string | null } | null;
}

export function isPlayedSchedule(row: object | null | undefined): boolean {
  if (!row) return false;
  const { status, match } = row as PlayableScheduleRow;
  const effective = match?.status || status || 'scheduled';
  return effective === 'completed' || !!match?.winner_id || effective === 'in_progress';
}

/**
 * Returns the row reading as played, for a delete the server refused as `match_played` while the
 * client still held a stale, unplayed copy. It sets the linked `match` status (the field
 * `isPlayedSchedule` reads first and that saves never send), never the row's own `status`, which
 * the save payloads carry and would otherwise overwrite on the server.
 */
export function markPlayed<T extends object>(row: T): T {
  if (isPlayedSchedule(row)) return row;
  const { match } = row as PlayableScheduleRow;
  return { ...row, match: { ...(match ?? {}), status: 'completed' } };
}

/**
 * Splits the board into what Reset / Clear division would unschedule (`removable`) and what they
 * keep because it was played (`kept`). `divisionId` narrows both to one division.
 */
export function countClearable(
  matches: Record<string, object> | object[],
  divisionId?: string,
): { removable: number; kept: number } {
  let removable = 0;
  let kept = 0;
  for (const match of Array.isArray(matches) ? matches : Object.values(matches)) {
    if (divisionId && (match as { event_group_id?: string }).event_group_id !== divisionId)
      continue;
    if (isPlayedSchedule(match)) kept += 1;
    else removable += 1;
  }
  return { removable, kept };
}
